import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  Select,
  Button,
  useDisclosure,
  Badge,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import { MdDelete, MdEdit, MdVisibility } from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from 'components/pagination';
import { useNavigate } from 'react-router-dom';
import AIAgentModal from './AIAgentModal';
import ErrorMessage from 'components/error-message';
import moment from 'moment';
import { createAPIEndPoint } from 'config/api/api';
import { logoutUser } from 'utils';
import DeleteModal from 'components/deleteModal/DeleteModal';
import toast from 'react-hot-toast';

const columnHelper = createColumnHelper();

export default function AIAgentsTable(props) {
  const { tableData, loading, fetchAgain } = props;
  const navigate = useNavigate();
  const [type, setType] = React.useState('add');
  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const [selectedId, setSelectedId] = React.useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const itemsPerPageOptions = [10, 20, 30, 50];
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const [isDeleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState()
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleCloseDeleteModal = () => setDeleteModalOpen(false);

  const handleDeleteItem = async () => {
    setIsDeleting(true);
    try {
      const response = await createAPIEndPoint('assistants/').delete(
        selectedItem.id,
      );
      fetchAgain(true)
      toast.success('Assistant deleted successfully!');
      setDeleteModalOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error deleting procedure');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    columnHelper.accessor((row, index) => index + 1, {
      id: 'id',
      header: () => <Text fontSize="12px" color="gray.400" fontWeight="bold">#</Text>,
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue()}</Text>,
    }),
    columnHelper.accessor('name', {
      id: 'name',
      header: () => <Text fontSize="12px" color="gray.400" fontWeight="bold">NAME</Text>,
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() || 'N/A'}</Text>,
    }),
    columnHelper.accessor('model', {
      id: 'model',
      header: () => <Text fontSize="12px" color="gray.400" fontWeight="bold">Model</Text>,
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() || 'N/A'}</Text>,
    }),
    columnHelper.accessor('temperature', {
      id: 'temperature',
      header: () => <Text fontSize="12px" color="gray.400" fontWeight="bold">Temperature</Text>,
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() || 'N/A'}</Text>,
    }),
    columnHelper.accessor('clinic_name', {
      id: 'clinic_name',
      header: () => <Text fontSize="12px" color="gray.400" fontWeight="bold">Clinic</Text>,
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() || 'N/A'}</Text>,
    }),

    columnHelper.accessor('service', {
      id: 'service',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Service
        </Text>
      ),
      cell: (info) => {
        const serviceColors = {
          openai: 'teal',
          deepgram: 'red',
        };

        const serviceLabels = {
          openai: 'OpenAI',
          deepgram: 'Deepgram',
        };

        const service = info.getValue();
        const color = serviceColors[service] || 'gray';
        const label = serviceLabels[service] || 'N/A';

        return (
          <Badge
            colorScheme={serviceColors[service] || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="60px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {label}
            </Text>
          </Badge>
        );
      },
    }),
    columnHelper.accessor('created_at', {
      id: 'created_at',
      header: () => <Text fontSize="12px" color="gray.400" fontWeight="bold">Created At</Text>,
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => <Text fontSize="12px" color="gray.400" fontWeight="bold">ACTION</Text>,
      cell: (info) => {
        const itemId = info.row.original.id;

        const handleOpenDeleteModal = () => {
          setSelectedItem(info.row.original)
          setDeleteModalOpen(true);
        }


        return (
          <Box display="flex">
            <IconButton
              icon={<MdEdit style={{ marginLeft: '-1px' }} />}
              aria-label="Edit"
              colorScheme="brand"
              size="sm"
              mr="6px"
              // onClick={() => {
              //   setSelectedId(itemId);
              //   setType('edit');
              //   onOpen();
              // }}
              onClick={() => navigate(`/admin/ai-agents/edit/${itemId}`, { state: { type: "edit" } })}

            />
            <IconButton
              icon={<MdVisibility style={{ marginLeft: '-1px' }} />}
              aria-label="View"
              colorScheme="blackAlpha"
              size="sm"
              mr="6px"
              onClick={() => navigate(`details/${itemId}`, { state: { id: itemId } })}
            />
            <IconButton
              icon={<MdDelete style={{ marginLeft: '-1px' }} />}
              aria-label="Delete"
              colorScheme="red"
              size="sm"
              onClick={handleOpenDeleteModal}
            />
          </Box>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const [clinics, setClinics] = React.useState([]);
  const [selectedClinicId, setSelectedClinicId] = React.useState('');

  const fetchClinics = async () => {
    try {
      const response = await createAPIEndPoint('clinic').fetchAll(); // Still fetching assistants
      setClinics(response.data.clinics || []);
    } catch (err) {
      if (err?.response?.data?.error?.includes('Bearer token')) {
        logoutUser(navigate);
      }
    }
  };

  React.useEffect(() => {
    fetchClinics();
  }, []);


  React.useEffect(() => {
    if (tableData?.length) setData([...tableData]);
  }, [tableData]);

  React.useEffect(() => {
    if (!tableData) return;

    let filtered = [...tableData];

    if (selectedClinicId) {
      filtered = filtered.filter((item) => item.clinic_id === Number(selectedClinicId));
    }

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.instructions?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setCurrentPage(1);
    setData(filtered);
  }, [searchTerm, selectedClinicId, tableData]);


  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(data.length / itemsPerPage);

  return (
    <>
      <Card flexDirection="column" w="100%" px="0px" overflowX="auto">
        <Flex
          px={{ base: '12px', md: '25px' }}
          mb="8px"
          justifyContent="space-between"
          align="center"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 2, md: 0 }}
          w="100%"
        >
          <Text
            color={textColor}
            fontSize="24px"
            fontWeight="700"
            lineHeight="100%"
            mb={{ base: '8px', md: '0px' }}
            textAlign={{ base: 'center', md: 'left' }}
          >
            Assistants
          </Text>


          <Box ml={{ base: 0, md: "auto" }} w={{ base: '100%', md: 'auto', }}>
            <Select
              placeholder="Filter by Clinic"
              variant="auth"
              fontSize="sm"
              fontWeight="500"
              value={selectedClinicId}
              onChange={(e) => {
                setSelectedClinicId(e.target.value);
                setCurrentPage(1);
              }}
            >
              {clinics.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.clinic_name}
                </option>
              ))}
            </Select>
          </Box>

          <Box ml={{ base: 0, md: 5 }} mr={{ base: 0, md: 5 }} w={{ base: '100%', md: 'auto' }}>
            <SearchBar width="100%" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </Box>

          <Box w={{ base: '100%', md: 'auto' }}>
            <Button
              w="100%"
              minW={{ base: 'auto', md: '140px' }}
              variant="brand"
              fontWeight="500"
              // onClick={() => {
              //   onOpen();
              //   setType('add');
              // }}
              onClick={() => navigate("/admin/ai-agents/new", { state: { type: "add" } })}
            >
              Add Assistant
            </Button>
          </Box>
        </Flex>

        <Flex
          px={{ base: '12px', md: '25px' }}
          mb="8px"
          justifyContent="space-between"
          align="center"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 2, md: 0 }}
          w="100%"
        >
          <Box w={{ base: '100%', md: 'auto' }}>
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              width={{ base: '100%', md: 'fit-content' }}
              borderRadius="16px"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>{option} per page</option>
              ))}
            </Select>
          </Box>

          <Box w={{ base: '100%', md: 'auto' }} textAlign={{ base: 'center', md: 'right' }}>
            <Pagination totalPages={totalPages} initialPage={currentPage} onPageChange={setCurrentPage} />
          </Box>
        </Flex>

        <Box overflowX="auto">
          <Table variant="striped" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex justifyContent="space-between" align="center">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {paginatedData.map((row, index) => (
                <Tr key={index}>
                  {table.getRowModel().rows.find((r) => r.original === row)?.getVisibleCells()?.map((cell) => (
                    <Td key={cell.id} fontSize="14px" borderColor="transparent">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {!loading && data.length === 0 && (
          <ErrorMessage source={searchTerm ? 'results' : 'assistants'} />
        )}
      </Card>

      <AIAgentModal
        isOpen={isOpen}
        onClose={onClose}
        type={type}
        assistantId={selectedId}
        fetchAgain={fetchAgain}
      />


      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteItem}
        selectedItem={selectedItem?.name} // Item name to be deleted
        isLoading={isDeleting}

      />
    </>
  );
}
